import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxSpinnerService } from 'ngx-spinner';
import { StorageOracleService } from './../../../../../core/services/storage-oracle.service';
import { HeaderService } from './../../../../../core/services/header.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { AwsCloudService } from '../../../../../core/services/aws-cloud.service';
import { DeploymentService } from 'src/app/core/services/deployment.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { CloudTypes, FileTypes } from 'src/app/core/models/globals';
import { editor } from 'monaco-editor';
import { StorageDataTransferService } from '../../../../../core/services/storage-data-transfer.service';
import { StorageStrategyFactory } from 'src/app/modules/storage/components/storage-data-transfer/storage-operation.factory';
import { AuthenticationService } from 'src/app/core/services/authentication.service';


@Component({
  selector: 'app-monaco-file-editor',
  templateUrl: './monaco-file-editor.component.html',
  styleUrls: ['./monaco-file-editor.component.scss'],
})
export class MonacoFileEditorComponent implements OnInit, OnDestroy {
  fileName: string = "";
  depName: string = this.data.depName;
  cloudType: string = this.data.cloudType;
  filePath: string = this.data.filePath;
  toogleDialog: boolean = false;
  toogleFullScreen: boolean = false;
  heading: string = "";
  isDarkMode: boolean = true;
  spinnerActive: boolean = false;
  isServiceActive: boolean = false;
  isSearchVisible: boolean = false;
  isExpanded = false;
  editorInstance: editor.IStandaloneCodeEditor;
  editorOptions = {
    theme: 'vs-dark',
    language: 'plaintext',
    automaticLayout: true,
    minimap: {
      enabled: true
    },
    find: {
      addExtraSpaceOnTop: false,
      autoFindInSelection: 'never',
      seedSearchStringFromSelection: true
    },
    fontSize: 14,
    fixedOverflowWidgets: true, // This helps with overlay positioning
    scrollBeyondLastLine: false,
  };
  editorContent = '';

  searchTerm: string = '';
  searchResults: editor.FindMatch[] = [];
  currentMatchIndex: number = 0;
  totalMatches: number = 0;
  openReplace: boolean = false;
  replaceText: string = '';

  editorForm = new FormGroup({
    content: new FormControl("", [Validators.required]),
    fileName: new FormControl("", [Validators.required])
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      depName: string,
      cloudType: string,
      filePath: string,
      headNodeIp: string,
      nodeName: string,
      routeViaSynchpc: boolean,
      isBlob?: boolean, // Add blob storage info
      currentStorageName: string,
      storageFactory: any,
    },
    public dialogRef: MatDialogRef<MonacoFileEditorComponent>,
    private authenticationService: AuthenticationService,
    private _headerService: HeaderService,
    private deploymentService: DeploymentService,
    private _http: HttpClient,
    private envs: EnvironmentService,
    private _snackBar: MatSnackBar,
    private utilityService: UtilityService,
    public _spinner: NgxSpinnerService,
  ) { }

  ngOnInit(): void {
    this.getFileContent(this.filePath);
    if (this.filePath != null) {
      if (!FileTypes.checkIfText(this.filePath, this.envs.envVariables.TextFileExtension)) {
        this.dialogRef.close(false);
        this._snackBar.open("File Can't be Edited. ( NOT A TEXT FILE )", "Ok", { duration: 3000 });
        return;
      }
      this.heading = "Edit File";
    } else {
      this.heading = "Create File";
    }
    this.utilityService.checkIfUtilityServiceActive(this.data.headNodeIp, this.depName)
      .subscribe(data => this.isServiceActive = data);

    if (this.filePath) {
      const extension = this.getFileExtension(this.filePath);
      this.setEditorLanguage(extension);
    }
  }

  ngOnDestroy(): void {
    if (this.editorInstance) {
      this.editorInstance.dispose();
    }
  }

  onInitEditor(editor: editor.IStandaloneCodeEditor): void {
    this.editorInstance = editor;
    this.editorInstance.onKeyDown((e) => {
      if (e.keyCode === 9) {
        this.isSearchVisible = false;
      }
    });
  }

  setEditorTheme(): void {
    this.editorOptions = {
      ...this.editorOptions,
      theme: this.isDarkMode ? 'vs-dark' : 'vs'
    };
  }

  getFileExtension(filePath: string): string {
    if (!filePath) return '';
    const parts = filePath.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : '';
  }

  setEditorLanguage(extension: string): void {
    let language = 'plaintext';

    const languageMap = {
      'js': 'javascript',
      'ts': 'typescript',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'json': 'json',
      'md': 'markdown',
      'py': 'python',
      'java': 'java',
      'c': 'c',
      'cpp': 'cpp',
      'cs': 'csharp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'sh': 'shell',
      'xml': 'xml',
      'sql': 'sql',
      'yaml': 'yaml',
      'yml': 'yaml',
      'txt': 'plaintext'
    };

    if (extension && languageMap[extension]) {
      language = languageMap[extension];
    }

    this.editorOptions = {
      ...this.editorOptions,
      language
    };
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSubmit(): void {
    // Get content from Monaco editor
    const content = this.editorInstance.getValue();
    this.editorForm.controls['content'].setValue(content);

    // Create file object for saving
    let fileType = this.fileName.split(".").slice(1).join("");
    let myBlob = new Blob([content + "\n"], { type: fileType });
    let file = new File([myBlob], `${this.fileName}`, {
      type: "text/plain",
      lastModified: this._headerService.getUserNewDate().getTime()
    });

    this.dialogRef.close(file);
  }

  getFileContent(filePath: string): void {
    if (!filePath) return;
    this.showSpinner();

    const context = {
      deploymentName: this.data.depName,
      nodeName: this.data.nodeName,
      currentPath: filePath.substring(0, filePath.lastIndexOf('/')),
      cloudType: this.data.cloudType,
      activeStore: '', 
      currentStorageName: this.data.currentStorageName,
      fileName: filePath.split('/').pop() || '',
      userInfo: {
        username: this.authenticationService.currentUserValue.username?.split("@")[0],
        role: 0,
        headNodeIP: this.data.headNodeIp,
        routeViaSynchpc: this.data.routeViaSynchpc
      }
    };

    this.data.storageFactory.viewFileData(filePath, context)
      .pipe(tap(() => this.hideSpinner()))
      .subscribe({
        next: (data) => {
          let content = '';
          
          if (typeof data === 'string') {
            content = data;
          } else if (data instanceof Blob) {
            const reader = new FileReader();
            reader.onload = (e) => {
              content = e.target?.result as string || '';
              this.setEditorContent(content, filePath);
            };
            reader.readAsText(data);
            return;
          } else if (data && data.FileData) {
            content = data.FileData.toString().split("<br />").join("\n");
          } else if (data && data.content) {
            content = data.content.toString();
          } else if (data != null) {
            content = String(data);
          } else {
            content = '';
          }
          
          this.setEditorContent(content, filePath);
        },
        error: (err) => {
          console.error('Error loading file content:', err);
          this._snackBar.open("Error loading file content", "Ok", { duration: 3000 });
        }
      });
  }



  toogleDialogMinMax(): void {
    if (this.toogleFullScreen && !this.toogleDialog) { this.onFullScreen(); }
    if (this.toogleDialog == false) {
      this.toogleDialog = true;
      let element = document.querySelector('.monaco-file-editor-dialog');
      element?.classList.add('minimize2');
    } else {
      this.toogleDialog = false;
      let element = document.querySelector('.monaco-file-editor-dialog');
      element?.classList.remove('minimize2');
    }
  }

  onFullScreen(): void {
    if (this.toogleDialog) { this.toogleDialogMinMax(); }
    let element = document.querySelector('.monaco-file-editor-dialog');
    if (!this.toogleFullScreen) {
      element?.classList.add('fullScreen');
    } else {
      element?.classList.remove('fullScreen');
    }
    this.toogleFullScreen = !this.toogleFullScreen;
  }

  private setEditorContent(content: string, filePath: string): void {
    this.editorContent = content;
    this.editorForm.controls["content"].setValue(this.editorContent);
    let fileName = filePath.split("/");
    this.fileName = fileName[fileName.length - 1];
  }

  // Monaco editor search functionality
  searchInEditor(): void {
    if (!this.searchTerm || !this.editorInstance) return;

    const model = this.editorInstance.getModel();
    if (!model) return;

    const findOptions = {
      caseSensitive: this.searchTerm === this.searchTerm.toLowerCase() ? false : true,
      matchCase: this.searchTerm !== this.searchTerm.toLowerCase(),
      isRegex: false,
      matchWholeWord: false
    };

    this.editorInstance.getAction('editor.action.startFindReplaceAction').run();
    this.editorInstance.focus();
  }

  findNext(): void {
    if (!this.editorInstance) return;
    this.editorInstance.getAction('editor.action.nextMatchFindAction').run();
  }


  toggleSearch() {
    if (!this.editorInstance) return;

    if (this.isSearchVisible) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  private openSearch() {
    this.editorInstance.focus();
    this.editorInstance.getAction('actions.find')?.run();
    this.isSearchVisible = true;
  }

  private closeSearch() {
    // Method 1: Use the correct command trigger
    try {
      this.editorInstance.trigger('keyboard', 'closeFindWidget', {});
      this.isSearchVisible = false;
      return;
    } catch (error) {
      console.log('Method 1 failed, trying method 2');
    }
  }

  findPrevious(): void {
    if (!this.editorInstance) return;
    this.editorInstance.getAction('editor.action.previousMatchFindAction').run();
  }

  replaceNext(): void {
    if (!this.editorInstance || !this.replaceText) return;
    this.editorInstance.getAction('editor.action.nextMatchFindAction').run();
    this.editorInstance.getAction('editor.action.replaceOne').run();
  }

  replaceAll(): void {
    if (!this.editorInstance || !this.replaceText) return;
    this.editorInstance.getAction('editor.action.replaceAll').run();
  }

  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    this.setEditorTheme();
  }

  showSpinner() {
    this.spinnerActive = true;
    return this._spinner.show('fileEditor');
  }

  hideSpinner() {
    this.spinnerActive = false;
    return this._spinner.hide('fileEditor');
  }
}
